import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { RouteProp, useRoute } from "@react-navigation/native";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { DietHistoryItem, DietService } from "../../../services/DietService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { useAuth } from "../../../../context/AuthContext";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type DietHistoryRouteProp = RouteProp<AuthStackParamList, "DietChartHistory">;

/* ---------------- SAFE DATE PARSER ---------------- */

function tryParseHistoryDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;

  const a = parse(dateStr, "dd-MM-yyyy", new Date());
  if (!isNaN(a.getTime())) return a;

  const b = parse(dateStr, "yyyy-MM-dd", new Date());
  if (!isNaN(b.getTime())) return b;

  const c = new Date(dateStr);
  if (!isNaN(c.getTime())) return c;

  return null;
}

/* ---------------- SCREEN ---------------- */

export function DietChartHistoryScreen() {
  const route = useRoute<DietHistoryRouteProp>();
  const { user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const initialRange = resolveHistoryRange(route.params, today);
  const [filterType, setFilterType] = useState<FilterType>(
    initialRange.hasNavigationDates ? "Custom" : "Monthly"
  );
  const [summary, setSummary] = useState({ isSelf: true });

  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);

  const [entries, setEntries] = useState<DietHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const effectiveUserId = useMemo(() => {
    return initialRange.devoteeId || user?.userId || "";
  }, [initialRange.devoteeId, user?.userId]);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDateLabel, setEditingDateLabel] = useState<string>("");
  const [editMorning, setEditMorning] = useState("");
  const [editAfternoon, setEditAfternoon] = useState("");
  const [editNight, setEditNight] = useState("");

  /* ---------------- FILTER ---------------- */

  const applyFilter = (type: FilterType) => {
    setFilterType(type);
    if (type === "Custom") return;

    const now = new Date();
    let start = startOfMonth(now);
    let end = endOfMonth(now);

    if (type === "Weekly") {
      start = startOfWeek(now);
      end = endOfWeek(now);
    } else if (type === "Quarterly") {
      start = startOfQuarter(now);
      end = endOfQuarter(now);
    } else if (type === "Yearly") {
      start = startOfYear(now);
      end = endOfYear(now);
    }

    setFromDate(format(start, "yyyy-MM-dd"));
    setToDate(format(end, "yyyy-MM-dd"));
  };

  const openPicker = (mode: "from" | "to") => {
    const value = new Date(mode === "from" ? fromDate : toDate);

    DateTimePickerAndroid.open({
      value,
      mode: "date",
      display: "calendar",
      onChange: (event, selectedDate) => {
        if (event.type !== "set" || !selectedDate) return;

        const v = format(selectedDate, "yyyy-MM-dd");
        mode === "from" ? setFromDate(v) : setToDate(v);
        setFilterType("Custom");
      },
    });
  };

  /* ---------------- LOAD ---------------- */

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const res = await DietService.getDietHistory({ userId: effectiveUserId, fromDate, toDate });
      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load history");
        setEntries([]);
        return;
      }
debugger
      setEntries((res.data.records || []) as DietHistoryItem[]);
      setSummary(res.data.response);

    } catch (e: any) {
      Flash.error(e?.message || "History load error");
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, fromDate, toDate]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  /* ---------------- EDIT ---------------- */

  const openEdit = async (item: DietHistoryItem) => {
    try {
      if (!item?.data.dietId) {
        Flash.error("DietId missing in history response");
        return;
      }

      setLoading(true);
      const res = await DietService.getDiet(item.data.dietId);

      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load diet");
        return;
      }

      const d = (res.data || [])[0];
      setEditingId(d?.dietId ?? item.data.dietId);
      setEditingDateLabel(d?.date ?? item.date);
      setEditMorning(d?.morning ?? "");
      setEditAfternoon(d?.afternoon ?? "");
      setEditNight(d?.night ?? "");
    } catch (e: any) {
      Flash.error(e?.message || "Failed to open edit");
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;

    try {
      setLoading(true);

      const dateObj = tryParseHistoryDate(editingDateLabel);

      const payload = {
        dietId: editingId,
        date: dateObj ? format(dateObj, "yyyy-MM-dd") : null,
        morning: editMorning?.trim() || null,
        afternoon: editAfternoon?.trim() || null,
        night: editNight?.trim() || null,
      };

      const res = await DietService.update(payload as any);

      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to update");
        return;
      }

      Flash.success("Success!", "Diet Updated Successfully");
      setEditingId(null);
      loadHistory();
    } catch (e: any) {
      Flash.error(e?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = (dietId?: string | null) => {
    if (!dietId) return;
    setPendingDeleteId(dietId);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadHistory();
    } finally {
      setRefreshing(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <DivineBackground overlayOpacity={0.95}>
      <Header title="Diet History" showBack showProfile showHome />

      <ScrollView
        contentContainerStyle={styles.page}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >
        {/* Filter Card */}
        <View style={styles.filterCard}>
          <View style={styles.filterHead}>
            <Text style={styles.filterIcon}>🔎</Text>
            <Text style={styles.filterTitle}>Filter History</Text>
          </View>

          {/* Filter Chips */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipRow}
          >
            {["Weekly", "Monthly", "Quarterly", "Yearly"].map((x) => (
              <Pressable
                key={x}
                onPress={() => applyFilter(x as FilterType)}
                style={[
                  styles.chip,
                  filterType === x ? styles.chipActive : styles.chipInactive,
                ]}
              >
                <Text
                  style={[
                    styles.chipText,
                    { color: filterType === x ? "#fff" : "#166534" },
                  ]}
                >
                  {x}
                </Text>
              </Pressable>
            ))}
          </ScrollView>

          {/* Date Inputs */}
          <View style={styles.dateRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>From Date</Text>
              <Pressable onPress={() => openPicker("from")} style={styles.dateInput}>
                <Text style={styles.dateIcon}>📅</Text>
                <Text style={styles.dateText}>
                  {format(new Date(fromDate), "dd-MM-yyyy")}
                </Text>
              </Pressable>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>To Date</Text>
              <Pressable onPress={() => openPicker("to")} style={styles.dateInput}>
                <Text style={styles.dateIcon}>📅</Text>
                <Text style={styles.dateText}>
                  {format(new Date(toDate), "dd-MM-yyyy")}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* List */}
        {loading ? (
          <ActivityIndicator />
        ) : entries.length === 0 ? (
          <Text style={{ textAlign: "center", marginTop: 30 }}>
            No records found.
          </Text>
        ) : (
          entries.map((x, idx) => {
            const dt = tryParseHistoryDate(x.date);

            return (
              <View key={(x.data.dietId ?? x.date) + idx} style={styles.itemCard}>
                {/* HEADER */}
                <View style={styles.itemTop}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                    <Text style={styles.itemDate}>
                      {dt ? format(dt, "EEE, dd MMM yyyy") : "-"}
                    </Text>
                  </View>

                  {summary.isSelf && (
                    <View style={styles.actions}>
                      {x.data?.isEditable ? (
                        <Pressable onPress={() => openEdit(x)} style={styles.iconBtn} disabled={loading}>
                          <Text>✏️</Text>
                        </Pressable>
                      ) : (
                        <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                      )}
                      <Pressable
                        onPress={() => deleteEntry(x.data.dietId)}
                        style={[styles.iconBtn, styles.iconBtnDanger]}
                        disabled={loading}
                      >
                        <Text style={{ color: "red" }}>🗑️</Text>
                      </Pressable>
                    </View>
                  )}
                </View>

                {/* MEALS */}
                <View style={{ marginTop: 12, gap: 10 }}>
                  <View style={[styles.mealRow, { backgroundColor: "rgba(16,185,129,0.10)" }]}>
                    <Text style={[styles.mealLabel, { color: "#166534" }]}>
                      MORNING
                    </Text>
                    <Text style={styles.mealValue}>
                      {x.data?.morning?.trim() ? x.data.morning : "-"}
                    </Text>
                  </View>

                  <View style={[styles.mealRow, { backgroundColor: "rgba(249,115,22,0.10)" }]}>
                    <Text style={[styles.mealLabel, { color: "#9A3412" }]}>
                      NOON
                    </Text>
                    <Text style={styles.mealValue}>
                      {x.data?.afternoon?.trim() ? x.data.afternoon : "-"}
                    </Text>
                  </View>

                  <View style={[styles.mealRow, { backgroundColor: "rgba(59,130,246,0.10)" }]}>
                    <Text style={[styles.mealLabel, { color: "#1D4ED8" }]}>
                      NIGHT
                    </Text>
                    <Text style={styles.mealValue}>
                      {x.data?.night?.trim() ? x.data.night : "-"}
                    </Text>
                  </View>
                </View>
              </View>
            );
          })
        )}
      </ScrollView>

      {/* Edit Modal */}
      <Modal
        transparent
        visible={!!editingId}
        animationType="fade"
        onRequestClose={() => {
          if (!loading) setEditingId(null);
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCardExact}>

            {/* HEADER */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitleExact}>Edit Diet Entry</Text>

              <Pressable onPress={() => setEditingId(null)} disabled={loading}>
                <Text style={styles.closeIcon}>✕</Text>
              </Pressable>
            </View>

            {/* DATE */}
            {(() => {
              const modalDateObj = tryParseHistoryDate(editingDateLabel);
              return (
                <Text style={styles.modalDateExact}>
                  Date: {modalDateObj ? format(modalDateObj, "dd MMMM yyyy") : "-"}
                </Text>
              );
            })()}

            {/* INPUTS */}
            <View style={{ gap: 10, marginTop: 12 }}>

              <View>
                <Text style={styles.inputLabelExact}>Morning Diet</Text>
                <Input
                  placeholder="What did you have?"
                  value={editMorning}
                  onChangeText={(text) => {
                    if (text.length <= 1000) setEditMorning(text);
                  }}
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  maxLength={1000}
                  inputStyle={{ height: 70 }}
                />

                <Text style={styles.charCount}>{editMorning.length}/1000</Text>
              </View>

              <View>
                <Text style={styles.inputLabelExact}>Noon Diet</Text>
                <Input
                  placeholder="What did you have?"
                  value={editAfternoon}
                  onChangeText={(text) => {
                    if (text.length <= 1000) setEditAfternoon(text);
                  }}
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  maxLength={1000}
                  inputStyle={{ height: 70 }}
                />

                <Text style={styles.charCount}>{editAfternoon.length}/1000</Text>
              </View>

              <View>
                <Text style={styles.inputLabelExact}>Night Diet</Text>
                <Input
                  placeholder="What did you have?"
                  value={editNight}
                  onChangeText={(text) => {
                    if (text.length <= 1000) setEditNight(text);
                  }}
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  maxLength={1000}
                  inputStyle={{ height: 70 }}
                />

                <Text style={styles.charCount}>{editNight.length}/1000</Text>
              </View>

            </View>

            {/* BUTTONS */}
            <View style={styles.modalButtonRowExact}>
              <Pressable
                style={styles.cancelBtnExact}
                onPress={() => setEditingId(null)}
                disabled={loading}
              >
                <Text style={styles.cancelTextExact}>Cancel</Text>
              </Pressable>

              <Pressable
                style={[styles.updateBtnExact, loading && styles.buttonDisabled]}
                onPress={saveEdit}
                disabled={loading}
              >
                <Text style={styles.updateTextExact}>{loading ? "Updating..." : "Update"}</Text>
              </Pressable>
            </View>

          </View>
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!pendingDeleteId}
        title="Delete Entry"
        message="Are you sure you want to delete this diet entry?"
        confirmText="Delete"
        destructive
        loading={loading}
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          try {
            setLoading(true);
            const res = await DietService.delete(pendingDeleteId);
            if (res?.status !== 200) {
              Flash.error(res?.message || "Failed to delete");
              return;
            }
            Flash.success("Success!", "Diet Deleted Successfully");
            setPendingDeleteId(null);
            loadHistory();
          } catch (e: any) {
            Flash.error(e?.message || "Delete failed");
          } finally {
            setLoading(false);
          }
        }}
      />
    </DivineBackground>
  );
}

/* ---------------- STYLES ---------------- */

const styles = StyleSheet.create({
  page: { padding: 14, paddingBottom: 40, gap: 12 },

  filterCard: {
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(22,163,74,0.18)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },

  filterHead: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },

  filterIcon: {
    fontSize: 16,
    color: "#16A34A",
  },

  filterTitle: {
    fontSize: 15,
    fontWeight: "900",
    color: "#1A2E1F",
  },

  chipRow: {
    gap: 10,
    paddingBottom: 8,
  },

  chip: {
    paddingHorizontal: 18,
    paddingVertical: 8,
    borderRadius: 999,
  },

  chipActive: {
    backgroundColor: "#16A34A",
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 3,
  },

  chipInactive: {
    backgroundColor: "rgba(22,163,74,0.12)",
  },

  chipText: {
    fontSize: 13,
    fontWeight: "900",
  },

  dateRow: {
    flexDirection: "row",
    gap: 14,
    marginTop: 14,
  },

  charCount: {
    fontSize: 11,
    color: "rgba(0,0,0,0.45)",
    textAlign: "right",
    marginTop: 4,
  },

  dateLabel: {
    fontSize: 11,
    fontWeight: "800",
    color: "rgba(26,71,49,0.60)",
    marginBottom: 6,
    marginLeft: 2,
  },

  dateInput: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },

  dateIcon: {
    fontSize: 14,
    opacity: 0.6,
  },

  dateText: {
    fontSize: 13.5,
    fontWeight: "800",
    color: "#111827",
  },

  dateBox: {
    flex: 1,
    padding: 10,
    borderRadius: 12,
    backgroundColor: "#F3F4F6",
    alignItems: "center",
  },

  mealRow: {
    borderRadius: 12,
    padding: 12,
  },

  mealLabel: {
    fontSize: 11,
    fontWeight: "900",
    textTransform: "uppercase",
  },

  mealValue: {
    marginTop: 4,
    fontSize: 13.5,
    fontWeight: "700",
    color: "rgba(17,24,39,0.85)",
  },

  itemCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(16,185,129,0.15)",
  },

  itemTop: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  itemDate: { fontWeight: "900" },
  actions: { flexDirection: "row", gap: 10 },
  iconBtn: {
    minWidth: 64,
    height: 34,
    paddingHorizontal: 10,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(22,163,74,0.10)",
  },
  iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
  iconBtnPlaceholder: { opacity: 0 },
  iconBtnText: { fontSize: 12, fontWeight: "900", color: "#166534" },
  iconBtnDangerText: { color: "#DC2626" },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },

  modalCardExact: {
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: 20,
    padding: 24,
    shadowColor: "#000",
    shadowOpacity: 0.25,
    shadowRadius: 25,
    shadowOffset: { width: 0, height: 15 },
    elevation: 10,
  },

  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  modalTitleExact: {
    fontSize: 18,
    fontWeight: "800",
    color: "#111827",
  },

  closeIcon: {
    fontSize: 18,
    color: "#6B7280",
    fontWeight: "600",
  },

  modalDateExact: {
    marginTop: 8,
    fontSize: 13,
    color: "#6B7280",
  },

  inputLabelExact: {
    fontSize: 13,
    fontWeight: "700",
    marginBottom: 6,
    color: "#374151",
  },

  modalButtonRowExact: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 26,
    gap: 14,
  },

  cancelBtnExact: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: "#F97316",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#ffffff",
  },

  cancelTextExact: {
    color: "#F97316",
    fontWeight: "700",
    fontSize: 15,
  },

  updateBtnExact: {
    flex: 1,
    height: 48,
    borderRadius: 12,
    backgroundColor: "#16A34A",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#16A34A",
    shadowOpacity: 0.4,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },

  updateTextExact: {
    color: "#ffffff",
    fontWeight: "800",
    fontSize: 15,
  },
  buttonDisabled: {
    opacity: 0.6,
  },

  modalCard: {
    backgroundColor: "#fff",
    borderRadius: 18,
    padding: 16,
  },
});
